import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  ANALYTICS_CHART_CARD_SX,
  ANALYTICS_CHART_INNER_BOX_SX,
  ANALYTICS_CHART_TITLE_SX,
} from './analyticsChartConfig';

/**
 * Shared chart card chrome with optional date-range / stack-by-type filters.
 */
export default function AnalyticsChartPanel({
  title,
  icon = null,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startLabel = 'Date Range Start',
  endLabel = 'Date Range End',
  stackByType,
  onStackByTypeChange,
  archivesLoading = false,
  chartHeight = 260,
  cardSx = {},
  fullHeight = false,
  filterLayout = 'wide',
  children,
}) {
  const hasDateFilters = onStartDateChange != null && onEndDateChange != null;
  const hasStackToggle = onStackByTypeChange != null;

  return (
    <Card
      sx={{
        ...ANALYTICS_CHART_CARD_SX,
        ...(fullHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}),
        ...cardSx,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={ANALYTICS_CHART_TITLE_SX}>
          {title}
          {icon}
        </Typography>

        {(hasDateFilters || hasStackToggle) && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {hasDateFilters && (
              <>
                <Grid size={filterLayout === 'wide' ? { xs: 12, sm: 6, md: 4, lg: 5 } : { xs: 12 }}>
                  <TextField
                    fullWidth
                    label={startLabel}
                    type="date"
                    value={startDate}
                    onChange={(e) => onStartDateChange(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={filterLayout === 'wide' ? { xs: 12, sm: 6, md: 4, lg: 5 } : { xs: 12 }}>
                  <TextField
                    fullWidth
                    label={endLabel}
                    type="date"
                    value={endDate}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
              </>
            )}
            {hasStackToggle && (
              <Grid
                size={filterLayout === 'wide' ? { xs: 12, md: 4, lg: 2 } : { xs: 12 }}
                display="flex"
                alignItems="center"
                justifyContent={filterLayout === 'wide' ? undefined : 'flex-end'}
              >
                {archivesLoading && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading...</Typography>
                  </Box>
                )}
                <FormControlLabel
                  sx={filterLayout === 'wide' ? { ml: 'auto' } : undefined}
                  control={
                    <Switch
                      checked={stackByType}
                      onChange={(e) => onStackByTypeChange(e.target.checked)}
                    />
                  }
                  label="Stack by type"
                />
              </Grid>
            )}
          </Grid>
        )}

        <Box sx={ANALYTICS_CHART_INNER_BOX_SX}>
          {archivesLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={chartHeight}>
              <CircularProgress />
            </Box>
          ) : (
            children
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
